import React, { useEffect, useRef, useState } from 'react';

import { MultiLineChartLine } from '../../MultiLineChart.types';
import * as S from './MultiLineChartLegend.styles';

type MultiLineChartLegendProps = {
  lines: MultiLineChartLine[];
};

const measureText = (text: string) => {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');

  if (!context) {
    return 0;
  }

  context.font = '400 12px nort';
  return context.measureText(text).width;
};

export const MultiLineChartLegend = ({ lines }: MultiLineChartLegendProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [multipleRows, setMultipleRows] = useState<MultiLineChartLine[][]>([]);

  const calculateLayout = () => {
    const gap = 32;
    const containerWidth = containerRef?.current?.clientWidth;

    if (!containerWidth) {
      return;
    }

    const itemsWidths = lines
      .map(line => measureText(line.label) + gap)
      .reduce((sum, acc) => sum + acc, 0);

    if (itemsWidths > containerWidth) {
      const isOdd = lines.length % 2 !== 0;
      // in case of odd legend number we want to have n+1 items in first row
      const midIndex = Math.floor(lines.length / 2) + (isOdd ? 1 : 0);
      setMultipleRows([lines.slice(0, midIndex), lines.slice(midIndex)]);
    } else {
      setMultipleRows([]);
    }
  };

  useEffect(() => {
    calculateLayout();

    window.addEventListener('resize', calculateLayout);
    return () => window.removeEventListener('resize', calculateLayout);
  }, [lines]);

  return (
    <S.LegendContainer
      ref={containerRef}
      $multipleRows={!!multipleRows.length}
      data-testid="multi-line-chart-legend"
    >
      {!multipleRows.length
        ? lines.map(row => (
            <S.LegendItem key={row.label}>
              <S.LegendColor $color={row.color} />
              <div>{row.label}</div>
            </S.LegendItem>
          ))
        : multipleRows.map((row, i) => (
            <S.MultiRowWrapper key={i}>
              {row.map(item => (
                <S.LegendItem key={item.label}>
                  <S.LegendColor $color={item.color} />
                  <div>{item.label}</div>
                </S.LegendItem>
              ))}
            </S.MultiRowWrapper>
          ))}
    </S.LegendContainer>
  );
};
